/**
 * admin/src/pages/ContentPage.tsx
 * Quản lý Quiz Catalog — Access Tier (free / member / premium) per lesson
 */

import { useState } from 'react';
import { useContent, useContentMutations, useCreateLesson } from '../hooks/useContent';
import {
  DataTable,
  Badge,
  Pagination,
  SearchInput,
  ErrorBanner,
  LoadingSpinner,
  Toggle,
} from '../components/ui/index';
import type { Column } from '../components/ui/index';
import type { AdminQuizCatalogRow, AccessTier, CreateLessonPayload } from '../types/admin';

type QuizRow = AdminQuizCatalogRow & Record<string, unknown>;

const PAGE_SIZE = 50;

export function ContentPage() {
  const [search, setSearch] = useState('');
  const [pathway, setPathway] = useState('');
  const [subject, setSubject] = useState('');
  const [skill, setSkill] = useState('');
  const [tierFilter, setTierFilter] = useState<AccessTier | ''>('');
  const [page, setPage] = useState(1);

  const {
    items: quizItems,
    total,
    totalPages,
    pathways,
    isLoading: contentLoading,
    error,
    refetch,
  } = useContent({ search, pathway, subject, skill, tier: tierFilter, page, limit: PAGE_SIZE });

  const { patchQuiz, savingId } = useContentMutations(refetch);
  const { createLesson, isCreating } = useCreateLesson(refetch);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<CreateLessonPayload>({
    quiz_id: '',
    title: '',
    pathway: 'cambridge',
    subject_slug: 'flyers',
    skill: 'listening',
    part: 1,
    level: 'Flyers',
    questions: 5,
    est_minutes: 5,
    access_tier: 'premium',
    image_url: '',
    difficulty: 'medium',
  });

  function resetForm() {
    setForm({
      quiz_id: '', title: '', pathway: 'cambridge', subject_slug: 'flyers',
      skill: 'listening', part: 1, level: 'Flyers', questions: 5,
      est_minutes: 5, access_tier: 'premium', image_url: '', difficulty: 'medium',
    });
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const ok = await createLesson({
      ...form,
      image_url: form.image_url || undefined,
    });
    if (ok) { setShowModal(false); resetForm(); }
  }

  const rows = quizItems as QuizRow[];

  // Reset page on filter changes
  function handleSearch(v: string) { setSearch(v); setPage(1); }
  function handlePathway(v: string) { setPathway(v); setPage(1); }
  function handleSubject(v: string) { setSubject(v); setPage(1); }
  function handleSkill(v: string) { setSkill(v); setPage(1); }
  function handleTier(v: string) { setTierFilter(v as AccessTier | ''); setPage(1); }

  const columns: Column<QuizRow>[] = [
    {
      key: 'title',
      label: 'Bài học',
      render: (row) => {
        const part = row.part as number | null | undefined;
        const skill = row.skill as string | undefined;
        return (
          <div>
            <div className="cell-primary">
              📖 {row.title as string}
            </div>
            <div className="cell-muted" style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span>{row.quiz_id as string}</span>
              {part != null && (
                <span style={{
                  background: 'var(--surface-raised, rgba(255,255,255,0.08))',
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                  padding: '0px 5px',
                  fontSize: 11,
                  color: 'var(--text-muted)',
                  whiteSpace: 'nowrap',
                }}>
                  Part {part}{skill ? ` · ${skill}` : ''}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'subject_slug',
      label: 'Môn',
      width: '110px',
      render: (row) => (
        <Badge variant="info">{row.subject_slug as string}</Badge>
      ),
    },
    {
      key: 'skill',
      label: 'Skill',
      width: '100px',
      render: (row) => (
        <Badge variant="info">{row.skill as string}</Badge>
      ),
    },
    {
      key: 'access_tier',
      label: 'Access',
      width: '150px',
      render: (row) => {
        const currentTier = (row.access_tier as string) || 'free';
        const isSaving = savingId === (row.quiz_id as string);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <select
              className="input"
              value={currentTier}
              disabled={isSaving}
              onChange={(e) =>
                patchQuiz(row.quiz_id as string, {
                  access_tier: e.target.value as AccessTier,
                })
              }
              style={{
                fontSize: 12,
                padding: '3px 6px',
                height: 28,
                minWidth: 100,
                // màu theo tier
                color:
                  currentTier === 'free' ? '#4ade80'
                  : currentTier === 'member' ? '#60a5fa'
                  : '#a78bfa',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                cursor: isSaving ? 'not-allowed' : 'pointer',
              }}
            >
              <option value="free">🟢 Free</option>
              <option value="member">🔵 Member</option>
              <option value="premium">🟣 Premium</option>
            </select>
            {isSaving && <LoadingSpinner size={14} />}
          </div>
        );
      },
    },
    {
      key: 'is_published',
      label: 'Published',
      width: '90px',
      render: (row) => (
        <Toggle
          checked={!!row.is_published}
          disabled={savingId === (row.quiz_id as string)}
          onChange={(checked) =>
            patchQuiz(row.quiz_id as string, { is_published: checked })
          }
        />
      ),
    },
  ];

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header__left">
          <h1>Quản lý Nội dung</h1>
          <p>Quản lý Access Tier (Free / Member / Premium) cho từng bài học</p>
        </div>
        <div className="page-header__right">
          <button
            className="btn btn-primary"
            id="btn-add-lesson"
            onClick={() => setShowModal(true)}
          >
            ➕ Thêm bài mới
          </button>
        </div>
      </div>

      {/* Modal thêm bài mới */}
      {showModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 16,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div className="card" style={{ width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ margin: 0 }}>➕ Thêm bài học mới</h2>
              <button className="btn" onClick={() => { setShowModal(false); resetForm(); }}>✕</button>
            </div>

            <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* quiz_id */}
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Quiz ID <span style={{color:'var(--color-danger)'}}>*</span></label>
                <input
                  className="input"
                  style={{ width: '100%' }}
                  placeholder="VD: FL1-MED-010"
                  value={form.quiz_id}
                  onChange={(e) => setForm(f => ({ ...f, quiz_id: e.target.value.toUpperCase() }))}
                  required
                  id="input-quiz-id"
                />
                <small style={{ color: 'var(--color-text-muted)' }}>Slug tự động tạo từ Quiz ID</small>
              </div>

              {/* title */}
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Tiêu đề <span style={{color:'var(--color-danger)'}}>*</span></label>
                <input
                  className="input"
                  style={{ width: '100%' }}
                  placeholder="VD: Set 8: 📚 A Day at School"
                  value={form.title}
                  onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                  required
                  id="input-title"
                />
              </div>

              {/* pathway + subject_slug */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Pathway <span style={{color:'var(--color-danger)'}}>*</span></label>
                  <select className="input" style={{ width: '100%' }} value={form.pathway}
                    onChange={(e) => setForm(f => ({ ...f, pathway: e.target.value }))} id="input-pathway">
                    <option value="cambridge">cambridge</option>
                    <option value="lop6">lop6</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Môn (subject_slug) <span style={{color:'var(--color-danger)'}}>*</span></label>
                  <select className="input" style={{ width: '100%' }} value={form.subject_slug}
                    onChange={(e) => setForm(f => ({ ...f, subject_slug: e.target.value }))} id="input-subject">
                    <option value="flyers">flyers</option>
                    <option value="movers">movers</option>
                    <option value="starters">starters</option>
                    <option value="ket">ket</option>
                    <option value="tieng-anh">tieng-anh</option>
                    <option value="toan">toan</option>
                  </select>
                </div>
              </div>

              {/* skill + part */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Skill <span style={{color:'var(--color-danger)'}}>*</span></label>
                  <select className="input" style={{ width: '100%' }} value={form.skill}
                    onChange={(e) => setForm(f => ({ ...f, skill: e.target.value }))} id="input-skill">
                    <option value="listening">listening</option>
                    <option value="reading">reading</option>
                    <option value="writing">writing</option>
                    <option value="math">math</option>
                    <option value="mixed">mixed</option>
                    <option value="science">science</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Part</label>
                  <input className="input" style={{ width: '100%' }} type="number" min={1} max={10}
                    value={form.part ?? 1}
                    onChange={(e) => setForm(f => ({ ...f, part: parseInt(e.target.value) || 1 }))}
                    id="input-part" />
                </div>
              </div>

              {/* level + difficulty */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Level</label>
                  <input className="input" style={{ width: '100%' }} placeholder="Flyers / Movers / ..."
                    value={form.level ?? ''}
                    onChange={(e) => setForm(f => ({ ...f, level: e.target.value }))}
                    id="input-level" />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Difficulty</label>
                  <select className="input" style={{ width: '100%' }} value={form.difficulty ?? 'medium'}
                    onChange={(e) => setForm(f => ({ ...f, difficulty: e.target.value }))} id="input-difficulty">
                    <option value="easy">easy</option>
                    <option value="medium">medium</option>
                    <option value="hard">hard</option>
                  </select>
                </div>
              </div>

              {/* questions + est_minutes */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Số câu hỏi</label>
                  <input className="input" style={{ width: '100%' }} type="number" min={1}
                    value={form.questions ?? 5}
                    onChange={(e) => setForm(f => ({ ...f, questions: parseInt(e.target.value) || 5 }))}
                    id="input-questions" />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Thời gian (phút)</label>
                  <input className="input" style={{ width: '100%' }} type="number" min={1}
                    value={form.est_minutes ?? 5}
                    onChange={(e) => setForm(f => ({ ...f, est_minutes: parseInt(e.target.value) || 5 }))}
                    id="input-minutes" />
                </div>
              </div>

              {/* access_tier */}
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Access Tier</label>
                <select className="input" style={{ width: '100%' }} value={form.access_tier ?? 'premium'}
                  onChange={(e) => setForm(f => ({ ...f, access_tier: e.target.value as AccessTier }))}
                  id="input-access-tier">
                  <option value="free">🟢 Free (tất cả vào được)</option>
                  <option value="member">🔵 Member (cần đăng nhập)</option>
                  <option value="premium">🟣 Premium (cần trả phí)</option>
                </select>
              </div>

              {/* image_url */}
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Image URL (tùy chọn)</label>
                <input className="input" style={{ width: '100%' }}
                  placeholder="https://cdn.luyenthi.io.vn/..."
                  value={form.image_url ?? ''}
                  onChange={(e) => setForm(f => ({ ...f, image_url: e.target.value }))}
                  id="input-image-url" />
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="button" className="btn" onClick={() => { setShowModal(false); resetForm(); }}
                  id="btn-cancel-create">Hủy</button>
                <button type="submit" className="btn btn-primary" disabled={isCreating} id="btn-submit-create">
                  {isCreating ? <LoadingSpinner size={16} /> : '➕ Tạo bài'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ marginBottom: 16 }}>
          <ErrorBanner message={error} onRetry={refetch} />
        </div>
      )}

      {/* ─── Toolbar ─────────────────────────────────────────────────── */}
      <div className="card" style={{ padding: '16px 20px', marginBottom: 0 }}>
        {/* Row 1: Search + Result count */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <SearchInput
            value={search}
            onChange={handleSearch}
            placeholder="Tìm quiz_id, tiêu đề..."
          />
          {/* Active filter chips */}
          {(pathway || subject || skill || tierFilter) && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {pathway && (
                <span style={{
                  fontSize: 11.5, padding: '3px 10px', borderRadius: 99,
                  background: 'var(--accent)', color: '#fff', fontWeight: 600, cursor: 'pointer',
                }} onClick={() => handlePathway('')}>
                  📂 {pathway} ×
                </span>
              )}
              {subject && (
                <span style={{
                  fontSize: 11.5, padding: '3px 10px', borderRadius: 99,
                  background: '#8b5cf6', color: '#fff', fontWeight: 600, cursor: 'pointer',
                }} onClick={() => handleSubject('')}>
                  📚 {subject} ×
                </span>
              )}
              {skill && (
                <span style={{
                  fontSize: 11.5, padding: '3px 10px', borderRadius: 99,
                  background: '#0ea5e9', color: '#fff', fontWeight: 600, cursor: 'pointer',
                }} onClick={() => handleSkill('')}>
                  🎯 {skill} ×
                </span>
              )}
              {tierFilter && (
                <span style={{
                  fontSize: 11.5, padding: '3px 10px', borderRadius: 99,
                  background: '#f59e0b', color: '#fff', fontWeight: 600, cursor: 'pointer',
                }} onClick={() => handleTier('')}>
                  🔑 {tierFilter} ×
                </span>
              )}
            </div>
          )}
          <span style={{
            marginLeft: 'auto', whiteSpace: 'nowrap',
            fontSize: 13, fontWeight: 700, color: 'var(--text-2)',
            background: 'var(--bg-elevated)', padding: '4px 12px', borderRadius: 99,
          }}>
            {total.toLocaleString('vi-VN')} bài học
          </span>
        </div>

        {/* Row 2: Cascading filters */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {/* GROUP 1: Nội dung */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              📂 Pathway
            </span>
            <select
              className="input"
              value={pathway}
              onChange={(e) => handlePathway(e.target.value)}
              style={{ minWidth: 130, fontSize: 13 }}
            >
              <option value="">Tất cả</option>
              {pathways.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          {/* GROUP 2: Môn */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              📚 Cấp độ / Môn
            </span>
            <select
              className="input"
              value={subject}
              onChange={(e) => handleSubject(e.target.value)}
              style={{ minWidth: 140, fontSize: 13 }}
            >
              <option value="">Tất cả</option>
              <optgroup label="── Cambridge ──">
                <option value="starters">🟢 Starters</option>
                <option value="movers">🔵 Movers</option>
                <option value="flyers">🟣 Flyers</option>
                <option value="ket">🟠 KET</option>
              </optgroup>
              <optgroup label="── Trường học ──">
                <option value="tieng-anh">📖 Tiếng Anh</option>
                <option value="toan">🔢 Toán</option>
              </optgroup>
            </select>
          </div>

          {/* GROUP 3: Skill */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              🎯 Kỹ năng
            </span>
            <select
              className="input"
              value={skill}
              onChange={(e) => handleSkill(e.target.value)}
              style={{ minWidth: 140, fontSize: 13 }}
            >
              <option value="">Tất cả kỹ năng</option>
              <option value="listening">🎧 Listening</option>
              <option value="reading">📖 Reading</option>
              <option value="writing">✍️ Writing</option>
              <option value="speaking">🗣️ Speaking</option>
              <option value="math">🔢 Math</option>
              <option value="mixed">🔀 Mixed</option>
              <option value="science">🔬 Science</option>
            </select>
          </div>

          {/* GROUP 4: Access Tier */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              🔑 Access Tier
            </span>
            <select
              className="input"
              value={tierFilter}
              onChange={(e) => handleTier(e.target.value)}
              style={{ minWidth: 130, fontSize: 13 }}
            >
              <option value="">Tất cả Tier</option>
              <option value="free">🟢 Free</option>
              <option value="member">🔵 Member</option>
              <option value="premium">🟣 Premium</option>
            </select>
          </div>

          {/* Reset button — chỉ hiện khi có filter đang active */}
          {(pathway || subject || skill || tierFilter || search) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 10.5, color: 'transparent' }}>reset</span>
              <button
                onClick={() => {
                  handleSearch('');
                  handlePathway('');
                  handleSubject('');
                  handleSkill('');
                  handleTier('');
                }}
                style={{
                  padding: '7px 14px', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)', background: 'transparent',
                  cursor: 'pointer', fontSize: 13, color: 'var(--text-2)',
                  whiteSpace: 'nowrap',
                }}
              >
                ✕ Xóa bộ lọc
              </button>
            </div>
          )}
        </div>
      </div>


      <div className="card" style={{ marginTop: 16 }}>
        <div className="table-wrapper">
          <DataTable<QuizRow>
            columns={columns}
            rows={rows}
            isLoading={contentLoading}
            emptyText="Không tìm thấy bài học nào"
          />
        </div>
        <Pagination
          page={page}
          totalPages={totalPages}
          totalRows={total}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          hasPrev={page > 1}
          hasNext={page < totalPages}
        />
      </div>
    </div>
  );
}

